import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForm = ({ inquiryType }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    organization: '',
    position: '',
    subject: '',
    message: '',
    preferredContact: '',
    urgency: '',
    newsletter: false,
    talentAlerts: false,
    terms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const countryOptions = [
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ng', label: 'Nigeria' },
    { value: 'gh', label: 'Ghana' },
    { value: 'ke', label: 'Kenya' },
    { value: 'za', label: 'South Africa' },
    { value: 'eg', label: 'Egypt' },
    { value: 'cm', label: 'Cameroon' },
    { value: 'ci', label: 'Ivory Coast' },
    { value: 'sn', label: 'Senegal' },
    { value: 'fr', label: 'France' },
    { value: 'de', label: 'Germany' },
    { value: 'es', label: 'Spain' },
    { value: 'it', label: 'Italy' },
    { value: 'pt', label: 'Portugal' },
    { value: 'nl', label: 'Netherlands' },
    { value: 'be', label: 'Belgium' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'video', label: 'Video Call' }
  ];

  const urgencyOptions = [
    { value: 'low', label: 'Low - General Inquiry' },
    { value: 'medium', label: 'Medium - Within 1 Week' },
    { value: 'high', label: 'High - Within 48 Hours' },
    { value: 'urgent', label: 'Urgent - Within 24 Hours' }
  ];

  const getInquirySpecificFields = () => {
    switch (inquiryType) {
      case 'club':
        return {
          organizationLabel: 'Club Name',
          organizationPlaceholder: 'Enter your club name',
          positionLabel: 'Your Role',
          positionPlaceholder: 'e.g., Chief Scout, Technical Director',
          subjectLabel: 'Inquiry Subject',
          subjectPlaceholder: 'e.g., Player Scouting, Transfer Inquiry'
        };
      case 'player':
        return {
          organizationLabel: 'Current Club/Academy',
          organizationPlaceholder: 'Enter your current club or academy',
          positionLabel: 'Playing Position',
          positionPlaceholder: 'e.g., Forward, Midfielder, Defender',
          subjectLabel: 'Inquiry Subject',
          subjectPlaceholder: 'e.g., Representation Request, Career Guidance'
        };
      case 'technical':
        return {
          organizationLabel: 'Current Organization',
          organizationPlaceholder: 'Enter your current organization',
          positionLabel: 'Coaching Role',
          positionPlaceholder: 'e.g., Head Coach, Assistant Coach, Analyst',
          subjectLabel: 'Inquiry Subject',
          subjectPlaceholder: 'e.g., Coaching Opportunity, Credential Verification'
        };
      case 'partner':
        return {
          organizationLabel: 'Company/Organization',
          organizationPlaceholder: 'Enter your company name',
          positionLabel: 'Your Position',
          positionPlaceholder: 'e.g., CEO, Partnership Manager',
          subjectLabel: 'Partnership Interest',
          subjectPlaceholder: 'e.g., Strategic Collaboration, Market Intelligence'
        };
      default:
        return {
          organizationLabel: 'Organization',
          organizationPlaceholder: 'Enter your organization',
          positionLabel: 'Position',
          positionPlaceholder: 'Enter your position',
          subjectLabel: 'Subject',
          subjectPlaceholder: 'Enter inquiry subject'
        };
    }
  };

  const fields = getInquirySpecificFields();

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.country) newErrors.country = 'Country is required';
    if (!formData?.organization?.trim()) newErrors.organization = `${fields?.organizationLabel} is required`;
    if (!formData?.position?.trim()) newErrors.position = `${fields?.positionLabel} is required`;
    if (!formData?.subject?.trim()) newErrors.subject = 'Subject is required';
    if (!formData?.message?.trim()) newErrors.message = 'Message is required';
    if (formData?.message?.trim()?.length < 50) newErrors.message = 'Message must be at least 50 characters';
    if (!formData?.preferredContact) newErrors.preferredContact = 'Preferred contact method is required';
    if (!formData?.urgency) newErrors.urgency = 'Urgency level is required';
    if (!formData?.terms) newErrors.terms = 'You must accept the terms and conditions';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        organization: '',
        position: '',
        subject: '',
        message: '',
        preferredContact: '',
        urgency: '',
        newsletter: false,
        talentAlerts: false,
        terms: false
      });
      setSubmitSuccess(false);
    }, 3000);
  };

  if (submitSuccess) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 md:p-8 lg:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
          <Icon name="CheckCircle2" size={40} className="text-success" />
        </div>
        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold font-headline text-foreground mb-3 md:mb-4">
          Message Sent Successfully!
        </h3>
        <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8 max-w-md mx-auto">
          Thank you for contacting ProX11Market. Our team will review your inquiry and respond within 24 hours based on your urgency level.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            iconName="Home"
            iconPosition="left"
            onClick={() => window.location.href = '/homepage'}
          >
            Return Home
          </Button>
          <Button
            variant="default"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => setSubmitSuccess(false)}
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8">
      <div className="mb-6 md:mb-8">
        <h3 className="text-xl md:text-2xl font-bold font-headline text-foreground mb-2">
          Contact Information
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Please provide your details so we can respond appropriately to your inquiry.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Input
          label="First Name"
          type="text"
          name="firstName"
          placeholder="Enter your first name"
          value={formData?.firstName}
          onChange={handleChange}
          error={errors?.firstName}
          required
        />

        <Input
          label="Last Name"
          type="text"
          name="lastName"
          placeholder="Enter your last name"
          value={formData?.lastName}
          onChange={handleChange}
          error={errors?.lastName}
          required
        />

        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="your.email@example.com"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          name="phone"
          placeholder="+44 20 1234 5678"
          value={formData?.phone}
          onChange={handleChange}
          error={errors?.phone}
          required
        />

        <Select
          label="Country"
          placeholder="Select your country"
          options={countryOptions}
          value={formData?.country}
          onChange={(value) => handleSelectChange('country', value)}
          error={errors?.country}
          searchable
          required
        />

        <Input
          label={fields?.organizationLabel}
          type="text"
          name="organization"
          placeholder={fields?.organizationPlaceholder}
          value={formData?.organization}
          onChange={handleChange}
          error={errors?.organization}
          required
        />

        <Input
          label={fields?.positionLabel}
          type="text"
          name="position"
          placeholder={fields?.positionPlaceholder}
          value={formData?.position}
          onChange={handleChange}
          error={errors?.position}
          required
        />

        <Input
          label={fields?.subjectLabel}
          type="text"
          name="subject"
          placeholder={fields?.subjectPlaceholder}
          value={formData?.subject}
          onChange={handleChange}
          error={errors?.subject}
          required
        />
      </div>
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Message <span className="text-error">*</span>
        </label>
        <textarea
          name="message"
          rows={6}
          placeholder="Please provide detailed information about your inquiry. Include any specific requirements, timelines, or questions you may have..."
          value={formData?.message}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
            errors?.message ? 'border-error' : 'border-border'
          }`}
        />
        {errors?.message && (
          <p className="mt-1 text-xs text-error flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {errors?.message}
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          Minimum 50 characters. Current: {formData?.message?.length}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Select
          label="Preferred Contact Method"
          placeholder="How should we contact you?"
          options={contactMethodOptions}
          value={formData?.preferredContact}
          onChange={(value) => handleSelectChange('preferredContact', value)}
          error={errors?.preferredContact}
          required
        />

        <Select
          label="Urgency Level"
          placeholder="Select urgency level"
          options={urgencyOptions}
          value={formData?.urgency}
          onChange={(value) => handleSelectChange('urgency', value)}
          error={errors?.urgency}
          required
        />
      </div>
      <div className="space-y-3 mb-6 p-4 bg-muted/30 rounded-lg">
        <p className="text-sm font-medium text-foreground mb-3">Communication Preferences</p>
        
        <Checkbox
          label="Subscribe to Newsletter"
          description="Receive weekly market insights, transfer trends, and industry updates"
          checked={formData?.newsletter}
          onChange={handleChange}
          name="newsletter"
        />

        <Checkbox
          label="Talent Alert Notifications"
          description="Get instant alerts about new player profiles and opportunities matching your criteria"
          checked={formData?.talentAlerts}
          onChange={handleChange}
          name="talentAlerts"
        />

        <Checkbox
          label="Terms and Conditions"
          description="I agree to the terms and conditions and privacy policy"
          checked={formData?.terms}
          onChange={handleChange}
          name="terms"
          error={errors?.terms}
          required
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
        >
          {isSubmitting ? 'Sending Message...' : 'Send Message'}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          size="lg"
          iconName="RotateCcw"
          iconPosition="left"
          onClick={() => {
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              country: '',
              organization: '',
              position: '',
              subject: '',
              message: '',
              preferredContact: '',
              urgency: '',
              newsletter: false,
              talentAlerts: false,
              terms: false
            });
            setErrors({});
          }}
          className="sm:w-auto"
        >
          Reset Form
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;